import { ReactNode } from "react";
import { css } from "@emotion/react";
import {
  ColorName,
  Colors,
  Icon,
  IconName,
  Link,
  Text,
  TooltipWrapper,
  Typography,
} from "@zapier/design-system";

import { FeatureListing, TooltipPosition } from "@/types";
import { emitFeatureClickEvent } from "@/observability/tracking/avroEventTracking/emitUserInteractionEvent";

type FeatureProps = {
  /** A single plan feature with optional tooltip. */
  feature: FeatureListing;
  /** The color of the icon to display. */
  iconColor?: ColorName;
  /** The name of the icon to display. */
  iconName?: IconName;
  /** True if should be a link component when feature owns a href */
  shouldBeLink?: boolean;
  /** The direction in which the tooltip should appear. */
  tooltipPosition?: TooltipPosition;
};

const rootStyles = css({
  cursor: "default",
  display: "flex",
  alignItems: "center",
});

const iconStyles = css({
  marginTop: "3px",
  marginRight: "5px",
  alignSelf: "baseline",
});

const tooltipStyles = css({ width: "250px" });

const rebrandFeatureNameStyles = css({
  ...Typography.paragraph3,
  color: Colors.neutral800,
  margin: 0,
  paddingRight: "8px",
  display: "flex",
  alignItems: "center",
  textUnderlineOffset: "3px",
});

const tooltipSpanStyles = css({
  position: "absolute",
  marginTop: "2px",
  marginLeft: "3px",
});

type FeatureContentProps = {
  name: string;
  href: string | null;
  hasTooltip: boolean;
};

const FeatureContent = ({ name, href, hasTooltip }: FeatureContentProps) => {
  const content = (
    <>
      <Text type="smallPrint1Medium" color={href ? "blue" : "neutral800"}>
        {name}
      </Text>
      {hasTooltip ? (
        <span css={tooltipSpanStyles}>
          <Icon color="neutral800" name="alertInfo" size={15} />
        </span>
      ) : null}
    </>
  );
  return (
    <div css={rebrandFeatureNameStyles} role="tooltip">
      {href ? (
        <Link
          href={href}
          target="_blank"
          onClick={() => {
            emitFeatureClickEvent({
              interactionRawText: name,
              destinationUrlAddress: href,
            });
          }}
          tabIndex={-1}
        >
          {content}
        </Link>
      ) : (
        <span>{content}</span>
      )}
    </div>
  );
};

type FeatureContentWrapperProps = {
  hasTooltip: boolean;
  tooltip: string | null;
  tooltipPosition: TooltipPosition;
  children: ReactNode;
};

const FeatureContentWrapper = ({
  hasTooltip,
  tooltip,
  tooltipPosition,
  children,
}: FeatureContentWrapperProps) => {
  if (hasTooltip) {
    return (
      <TooltipWrapper
        allowMultilineTooltip={true}
        content={<div css={tooltipStyles}>{tooltip}</div>}
        position={tooltipPosition}
      >
        {() => children}
      </TooltipWrapper>
    );
  }

  return <div>{children}</div>;
};

/**
 * A component that displays a single plan feature with optional tooltip.
 */
export const Feature = ({
  feature,
  iconColor = "success400",
  iconName = "formCheck",
  tooltipPosition = "north",
}: FeatureProps) => {
  const hasTooltip = !!feature.tooltip;

  return (
    <div css={rootStyles}>
      <div css={iconStyles}>
        <Icon color={iconColor} name={iconName} size={20} />
      </div>
      <FeatureContentWrapper
        hasTooltip={hasTooltip}
        tooltip={feature.tooltip}
        tooltipPosition={tooltipPosition}
      >
        <FeatureContent
          name={feature.name}
          href={feature.href}
          hasTooltip={hasTooltip}
        />
      </FeatureContentWrapper>
    </div>
  );
};
